import { useState, type ReactNode } from "react";
import { BadgeCheck, Calendar, Check, ChevronRight, Hash, Search, Type } from "lucide-react";
import type { CatalogDimension, CatalogMetric, PackCatalog } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Input, Tip } from "../../components/ui/primitives";
import { fieldList, type FieldRow, type FieldSection } from "./fieldList";
import { useBuilder } from "./store";

const dimIcon = (d: CatalogDimension) => (d.type === "time" ? Calendar : d.type === "number" ? Hash : Type);

/**
 * Left pane: searchable metrics and dimensions, from every data pack in the workspace. Fields that
 * can't be added (another pack's, since a widget reads one pack, or ones that would double-count with
 * what's chosen) collapse under each section, grouped by why. With one pack (most workspaces) there
 * are no pack headings.
 */
export function CatalogPane({ packs }: { packs: PackCatalog[] }) {
  const { draft, update } = useBuilder();
  const [q, setQ] = useState("");
  const { active, metrics, dimensions } = fieldList(packs, draft, q);
  const several = packs.length > 1;
  const isActive = (pack: PackCatalog) => pack.name === active.name;
  // In a one-pack workspace the query names no pack, so its widgets save as they always have.
  const packOf = (pack: PackCatalog) => (several ? pack.name : undefined);

  const toggleMetric = (pack: PackCatalog, m: CatalogMetric) =>
    update((dr) => {
      const has = isActive(pack) && dr.metrics.some((x) => x.kind === "metric" && x.ref === m.ref);
      return { ...dr, pack: packOf(pack), metrics: has ? dr.metrics.filter((x) => !(x.kind === "metric" && x.ref === m.ref)) : [...dr.metrics, { kind: "metric", ref: m.ref }] };
    });
  const toggleDim = (pack: PackCatalog, d: CatalogDimension) =>
    update((dr) => {
      const has = isActive(pack) && dr.dimensions.some((x) => x.ref === d.ref);
      return {
        ...dr,
        pack: packOf(pack),
        dimensions: has ? dr.dimensions.filter((x) => x.ref !== d.ref) : [...dr.dimensions, d.type === "time" ? { ref: d.ref, grain: "day" } : { ref: d.ref }],
        sort: dr.sort.filter((s) => !s.key.startsWith(d.ref)),
      };
    });

  const metricRow = (r: FieldRow<CatalogMetric>) => (
    <Tip key={`${r.pack.name}:${r.field.ref}`} content={r.blocker?.detail ?? r.field.description} side="right">
      <button
        disabled={!!r.blocker}
        onClick={() => toggleMetric(r.pack, r.field)}
        className={cn(
          "group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm",
          r.selected ? "bg-accent-soft text-foreground" : "hover:bg-surface-2",
          r.blocker && "cursor-not-allowed text-subtle hover:bg-transparent",
        )}
      >
        <span className={cn("flex size-4 shrink-0 items-center justify-center rounded border", r.selected ? "border-accent bg-accent text-white" : "border-border-strong", r.blocker && "opacity-40")}>
          {r.selected && <Check className="size-3" strokeWidth={3} />}
        </span>
        <span className="flex-1 truncate">{r.label}</span>
        {r.field.certified && <BadgeCheck className={cn("size-3.5 shrink-0 text-good", r.blocker && "opacity-40")} aria-label="Certified" />}
      </button>
    </Tip>
  );
  const dimRow = (r: FieldRow<CatalogDimension>) => (
    <DimensionRow key={`${r.pack.name}:${r.field.ref}`} row={r} onClick={() => toggleDim(r.pack, r.field)} />
  );

  return (
    <div className="flex h-full flex-col">
      <div className="p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-subtle" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search metrics & fields" className="pl-8" />
        </div>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto px-2 pb-4">
        <PickerSection title="Metrics" section={metrics} several={several} searching={!!q} row={metricRow} />
        <PickerSection title="Group by" section={dimensions} several={several} searching={!!q} row={dimRow} />
      </div>
    </div>
  );
}

/** A grouping in a field list; also the breakdown panel's "split by" choices. */
export function DimensionRow({ row: r, onClick }: { row: FieldRow<CatalogDimension>; onClick: () => void }) {
  const Icon = dimIcon(r.field);
  return (
    <Tip content={r.blocker?.detail ?? r.field.description} side="right">
      <button
        disabled={!!r.blocker}
        onClick={onClick}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm",
          r.selected ? "bg-accent-soft" : "hover:bg-surface-2",
          r.blocker && "cursor-not-allowed text-subtle hover:bg-transparent",
        )}
      >
        <Icon className={cn("size-3.5 shrink-0", r.selected ? "text-accent" : "text-subtle", r.blocker && "opacity-40")} />
        <span className="flex-1 truncate">{r.label}</span>
        {r.selected && <Check className="size-3.5 text-accent" />}
      </button>
    </Tip>
  );
}

/** One section of a field list: available fields by pack and model, the rest collapsed by why. */
export function PickerSection<F extends { ref: string }>({ title, section, several, searching, row }: {
  title: string;
  section: FieldSection<F>;
  several: boolean;
  searching: boolean;
  row: (r: FieldRow<F>) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const blockedCount = section.blocked.reduce((n, [, rs]) => n + rs.length, 0);
  // A search that only finds unavailable fields shows them, so it doesn't look like nothing matched.
  const open = expanded || (searching && !section.available.length);

  return (
    <section>
      <h3 className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">{title}</h3>
      {section.available.map(({ pack, models }) => (
        <div key={pack.name}>
          {several && <div className="px-2 pt-1 pb-0.5 text-[11px] font-semibold text-foreground">{pack.label}</div>}
          {models.map(([model, rows]) => (
            <div key={model} className="mb-2">
              <div className="px-2 py-1 text-xs font-medium text-muted">{model}</div>
              {rows.map(row)}
            </div>
          ))}
        </div>
      ))}
      {blockedCount > 0 && (
        <div className="mt-1">
          <button
            onClick={() => setExpanded(!open)}
            aria-expanded={open}
            className="flex w-full items-center gap-1 rounded-lg px-2 py-1 text-left text-xs text-subtle hover:text-foreground"
          >
            <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} />
            Not available with your selection ({blockedCount})
          </button>
          {open && section.blocked.map(([reason, rows]) => (
            <div key={reason} className="mb-2 pl-3">
              <div className="px-2 py-1 text-xs font-medium text-subtle">{reason}</div>
              {rows.map(row)}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
